import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyTriggerResolution } from './companyTriggerApi';
import SuppressedPanel from './SuppressedPanel';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import {
  STATUS_RAMP,
  RESOLVE_RAMP,
  BACKLOG_RAMP,
  VOLUME_RAMP,
  areaColor
} from './triggerTheme';

/**
 * Trigger Trace → Resolution.
 *
 * The closing act. Logging a trigger is the easy half of this feature; coming
 * back to mark it worked through is the half that means something changed, and
 * this page is entirely about that second half.
 *
 * WHAT TO READ HERE, IN ORDER:
 *
 *  1. HOW MUCH GETS CLOSED AT ALL. A tool where everything is logged and
 *     nothing is ever closed is a tool people are venting into, not working
 *     with. That is still worth something, but it is a different product than
 *     the one that was bought, and only this figure tells them apart.
 *  2. HOW LONG IT TAKES. Same-day resolutions are people using the feature as
 *     designed — name it, sit with it, close it. A long tail means things are
 *     being carried for weeks.
 *  3. THE STILL-OPEN PILE, AND HOW OLD IT IS. A big backlog of recent logs is
 *     normal. A backlog months deep is people who opened something and never
 *     came back to it, and it is the clearest signal in this whole section that
 *     support would land well.
 *  4. WHETHER THE HARD ONES CLOSE. If the resolution rate falls away at the
 *     severe end of the intensity scale, the people struggling most are the
 *     ones the tool is helping least.
 *
 * "Could not shake it" is the person's own honest mark on their own log. It is
 * never called a failure anywhere on this page, and it is deliberately amber
 * rather than red, because it is not one.
 *
 * `fetcher` is a prop so an unscoped hub could render this page unchanged.
 */
const TriggerResolution = ({ fetcher = fetchCompanyTriggerResolution }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (loader) return <Loader />;
  if (!stats || stats.suppressed) return <SuppressedPanel minimum={stats?.minimum ?? 0} />;

  const orDash = (value, suffix = '') => (value == null ? '-' : `${value}${suffix}`);

  const intensityRows = stats.resolutionByIntensity.filter((row) => row.resolutionRate != null);

  const intensityOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.greenDeep],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: intensityRows.map((row) => row.label) },
    yaxis: { max: 100, labels: { formatter: (val) => `${Math.round(val)}%` } },
    tooltip: {
      y: {
        formatter: (val, { dataPointIndex }) =>
          `${val}% worked through — from ${formatBigNumber(intensityRows[dataPointIndex]?.logs || 0)} logs`
      }
    }
  };

  const backlogHasData = stats.backlogSplit.some((row) => row.count > 0);
  const staleBacklog = stats.backlogSplit
    .filter((row) => row.label === '1–3 months' || row.label === 'Over 3 months')
    .reduce((sum, row) => sum + row.count, 0);

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        {/* Of everything logged, how much people ever came back and closed. */}
        <StatCard
          icon={STAT_ICONS.completions}
          title="Ever Closed"
          value={stats.logs > 0 ? `${stats.closureRate}%` : '-'}
        />
        {/* Of what they closed, how much they got past. */}
        <StatCard
          icon={STAT_ICONS.score}
          title="Worked Through"
          value={stats.closed > 0 ? `${stats.resolutionRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Average Days to Close"
          value={stats.avgDaysToResolve == null ? '-' : stats.avgDaysToResolve}
        />
        <StatCard
          icon={STAT_ICONS.more}
          title="Still Open"
          value={formatBigNumber(stats.pending)}
        />
        {/* The single most actionable number on the page. */}
        <StatCard
          icon={STAT_ICONS.streak}
          title="Open Over a Month"
          value={formatBigNumber(staleBacklog)}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Triggers Logged"
          value={formatBigNumber(stats.logs)}
        />
      </div>

      {/* ---------- Where things end up ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="What Happens Next"
          subtitle="Every trigger logged, by where it ended up"
          data={stats.statusSplit}
          colors={STATUS_RAMP}
          unit="logs"
          totalLabel="Logs"
          legendLimit={3}
        />
        <MiniDonutCard
          title="How Long It Took"
          subtitle="From logging it to marking it worked through"
          data={stats.timeToResolveSplit}
          colors={RESOLVE_RAMP}
          unit="logs"
          totalLabel="Closed"
          legendLimit={5}
        />
        <Panel title="How Old the Open Ones Are" subtitle="Still-open triggers, by how long they have sat">
          {backlogHasData ? (
            <Chart
              type="bar"
              height={260}
              width="100%"
              series={[{ name: 'Still open', data: stats.backlogSplit.map((row) => row.count) }]}
              options={{
                chart: { ...CHART_BASE, type: 'bar' },
                colors: [({ dataPointIndex }) => BACKLOG_RAMP[dataPointIndex] || CHART_COLORS.gray],
                plotOptions: {
                  bar: { borderRadius: 4, columnWidth: '45%', distributed: true }
                },
                dataLabels: { enabled: false },
                legend: { show: false },
                grid: CHART_GRID,
                xaxis: { categories: stats.backlogSplit.map((row) => row.label) },
                yaxis: { labels: { formatter: (val) => formatBigNumber(Math.round(val)) } },
                tooltip: { y: { formatter: (val) => `${formatBigNumber(val)} still open` } }
              }}
            />
          ) : (
            <PanelEmpty>Nothing is currently open.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Do the hard ones close? ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-6">
        <Panel
          title="Do the Hardest Ones Get Worked Through?"
          subtitle="Resolution rate against how hard the trigger hit"
        >
          {intensityRows.length > 0 ? (
            <>
              <Chart
                type="bar"
                height={300}
                width="100%"
                series={[
                  { name: 'Worked through', data: intensityRows.map((row) => row.resolutionRate) }
                ]}
                options={intensityOptions}
              />
              {/* The shape matters more than any single bar here. */}
              <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                If this falls away towards the right, the people having the hardest time are the
                ones getting least out of the tool — that is where extra support would land.
              </p>
            </>
          ) : (
            <PanelEmpty>
              No intensity band has enough closed logs behind it to report a rate.
            </PanelEmpty>
          )}
        </Panel>
        <Panel
          title="Which Areas of Life Get Resolved"
          subtitle="How much of each part of life people get past"
        >
          {stats.areaBreakdown.length > 0 ? (
            <>
              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Part of life</th>
                    <th className="py-2 px-4 font-medium text-center">Logs</th>
                    <th className="py-2 px-4 font-medium text-center">Worked through</th>
                    <th className="py-2 font-medium text-right">Still open</th>
                  </>
                }
              >
                {stats.areaBreakdown.map((row) => (
                  <tr key={row.label} className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: areaColor(row.label) }}
                        />
                        {row.label}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-center">{formatBigNumber(row.logs)}</td>
                    <td className="py-2 px-4 text-center">{orDash(row.resolutionRate, '%')}</td>
                    <td className="py-2 text-right">{orDash(row.openRate, '%')}</td>
                  </tr>
                ))}
              </PanelTable>
              <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                A dash means fewer than {stats.cellMinimum} logs sit behind that figure, so it is
                withheld rather than shown.
              </p>
            </>
          ) : (
            <PanelEmpty>Nothing logged yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Who keeps going ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <MiniDonutCard
          title="How Much People Log"
          subtitle="Triggers logged per person, all time"
          data={stats.volumeSplit}
          colors={VOLUME_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={5}
        />
        <MiniDonutCard
          title="Days Logged Recently"
          subtitle="Distinct days each person logged — last 30"
          data={stats.activeDaySplit}
          colors={VOLUME_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={5}
        />
      </div>

      <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
        &ldquo;Ever closed&rdquo; is the share of all logged triggers people came back to and marked
        one way or the other. &ldquo;Worked through&rdquo; is the share of those closed ones marked
        resolved — still-open triggers are excluded from it, because an open one is unfinished, not
        failed. &ldquo;Could not shake it&rdquo; is somebody&apos;s own honest note on their own
        log, entered for their benefit and not as a performance measure.
      </p>
      <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
        Nobody is named on this page. What people wrote about each moment is never read by this
        dashboard.
      </p>
    </div>
  );
};

TriggerResolution.propTypes = {
  fetcher: PropTypes.func
};

export default TriggerResolution;
